'use client';

import React from 'react';

import { Body, Caption } from '@depop/web-thrift-ui/Typography';
import clsx from 'clsx';

import styles from './styles.module.css';

import { AppBadges } from '@/components/AppBadge/AppBadges';
import { useAppBadgesMethods } from '@/components/AppBadge/useAppBadgesMethods';
import { DownloadAppQRCode } from '@/components/DownloadAppQRCode';
import { ExtendedLink } from '@/components/ExtendedLink';
import { buildStaticUrl } from '@/constants/endpoints';
import { type AppDownloadPlacement } from '@/modules/branch/placements';
import { useUserAgent } from '@/modules/device/useUserAgent';

type Props = {
  subtitle: string;
  placement: AppDownloadPlacement;
  title?: string;
  href?: string;
  className?: string;
};

function AppDownloadCard({
  subtitle,
  placement,
  title,
  href,
  className,
}: Props) {
  const { getIosAppBadge, getAndroidAppBadge, handleBadgeClick } =
    useAppBadgesMethods();
  const { device } = useUserAgent();

  const card = (
    <div
      className={clsx(styles.card, {
        [styles['card--titled']]: Boolean(title),
      })}
    >
      <img
        className={styles.appIcon}
        src={buildStaticUrl('/web/assets/depop-logo-white.png')}
        alt=""
        aria-hidden
      />
      <div
        className={clsx(styles.content, {
          [styles['content--compact']]: !title,
          [styles['content--titled']]: Boolean(title),
        })}
      >
        <div className={styles.textContainer}>
          {title ? (
            <>
              <Caption
                variant="lg-bold"
                colour="base"
                className={styles.mobileText}
              >
                {title}
              </Caption>
              <Caption
                variant="lg-regular"
                colour="base"
                className={styles.mobileText}
              >
                {subtitle}
              </Caption>
              <Body variant="sm-bold" className={styles.desktopText}>
                {title}
              </Body>
              <Body variant="sm-regular" className={styles.desktopText}>
                {subtitle}
              </Body>
            </>
          ) : (
            <Caption variant="lg-regular" colour="base">
              {subtitle}
            </Caption>
          )}
        </div>
        <div className={styles.appBadgesContainer}>
          {href ? (
            <span className={styles.badges}>
              {getIosAppBadge().badge}
              {getAndroidAppBadge().badge}
            </span>
          ) : (
            <AppBadges trackingId={placement} className={styles.badges} />
          )}
        </div>
      </div>
      <div className={styles.qrCodeContainer}>
        <DownloadAppQRCode size="sm" withDepopLogo placement={placement} />
      </div>
    </div>
  );

  if (href) {
    return (
      <ExtendedLink
        href={href}
        onClick={() => handleBadgeClick(device, placement)}
        className={className}
      >
        {card}
      </ExtendedLink>
    );
  }

  return <div className={className}>{card}</div>;
}

export { AppDownloadCard };
